import { useCallback, useContext, useEffect, useMemo } from "react";
import { Box, Skeleton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import NonIdealNew from "../../../../common/NonIdealNew";
import { ParagraphText } from "../../../../../Libraries";
import ReportPageHeader from "../ReportPageHeader";
import { FilterNonIdeal } from "../../../../common";
import pageStyles from "../styles.module.css";
import { LanguageContext } from "../../../../../context/LanguageContext";
import { useReports } from "../../../../../hooks/useReports";
import { useAuthContext } from "../../../../../context/AuthContext";
import { formatReportCurrency, getStartAndEndDate, REPORT_DATE_FILTERS } from "../../../../../utils";
import dayjs from "dayjs";
import useReportExport from "../../../../../hooks/useReportExport";
import useReportFilter from "../../../../../hooks/useReportFilter";
import { analytics, triggerLogEvent } from "../../../../../config/firebase";

const SKELETON_ROWS = 3;

const TotalSalesByChannel = () => {
  const [I18n] = useContext(LanguageContext);
  const { handleFetchReport, loading, salesByChannel } = useReports(I18n);
  const { business } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchReport = useCallback((start, end) => {
    handleFetchReport({
      business_id: business?.id,
      report_type: "sales_by_channel",
      start_date: dayjs(start?.toDate ? start.toDate() : start).toISOString(),
      end_date: dayjs(end?.toDate ? end.toDate() : end).toISOString(),
    });
  }, [handleFetchReport, business?.id]);


  useEffect(() => {
    triggerLogEvent(analytics, "report_sales_by_channel_visited", {});
  }, []);

  const { handleExport, exportLoading } = useReportExport("sales_by_channel");
  const { headerDefaultFilter, isYearlyFilter, resetCount, handleFilterDate, handleResetFilter } = useReportFilter(fetchReport, "sales_by_channel");

  const showNonIdeal = isYearlyFilter && !loading && salesByChannel.length === 0;

  const minDate = useMemo(() => dayjs().subtract(1, "year"), []);
  const maxDate = useMemo(() => dayjs().endOf("day"), []);

  return (
    <Box id="total_sales_by_channel_page_wrapper" className={pageStyles.pageWrapper}>
      <ReportPageHeader
        title={I18n.total_sales_by_channel}
        id="total_sales_by_channel"
        dateFilters={REPORT_DATE_FILTERS}
        onFilterDate={handleFilterDate}
        minDate={minDate}
        maxDate={maxDate}
        defaultFilter={headerDefaultFilter}
        resetKey={resetCount}
        hideActions={showNonIdeal}
        onExport={handleExport}
        exportLoading={exportLoading}
      />

      {showNonIdeal ? (
        <Box id="total_sales_by_channel_nonideal_body" className={pageStyles.pageBody}>
          <NonIdealNew
            buttonOneId="total_sales_by_channel_nonideal_start_sale_btn"
            title={I18n.no_data_yet}
            subtitle={I18n.it_looks_like_you_dont_have_any_data_yet}
            button1Title={I18n.start_new_sale}
            onButton1Click={() => {
              triggerLogEvent(analytics, "report_nonideal_start_new_sale", { report_type: "sales_by_channel" });
              navigate("/QuickSale/QuickSaleForm", { state: { pathname: location.pathname } });
            }}
            loading={false}
            disabledOne={false}
          />
        </Box>
      ) : (
        <>
          {(loading || salesByChannel.length > 0) && (
            <Box id="total_sales_by_channel_table_header" className="invoiceListTableHeader">
              <Box id="total_sales_by_channel_header_channel" className="invListTableHeaderCol sixColumn textLeft">
                <ParagraphText id="total_sales_by_channel_header_label_channel" className="fontSize12 fontWeight500 newLightColor">{I18n.channel}</ParagraphText>
              </Box>
              <Box id="total_sales_by_channel_header_orders" className="invListTableHeaderCol sixColumn textLeft">
                <ParagraphText id="total_sales_by_channel_header_label_orders" className="fontSize12 fontWeight500 newLightColor">{I18n.orders}</ParagraphText>
              </Box>
              <Box id="total_sales_by_channel_header_units_sold" className="invListTableHeaderCol sixColumn textLeft">
                <ParagraphText id="total_sales_by_channel_header_label_units_sold" className="fontSize12 fontWeight500 newLightColor">{I18n.units_sold}</ParagraphText>
              </Box>
              <Box id="total_sales_by_channel_header_total_sales" className="invListTableHeaderCol sixColumn textRight">
                <ParagraphText id="total_sales_by_channel_header_label_total_sales" className="fontSize12 fontWeight500 newLightColor">{I18n.total_sales}</ParagraphText>
              </Box>
              <Box id="total_sales_by_channel_header_avg_order_value" className="invListTableHeaderCol sixColumn textRight">
                <ParagraphText id="total_sales_by_channel_header_label_avg_order_value" className="fontSize12 fontWeight500 newLightColor">{I18n.avg_order_value}</ParagraphText>
              </Box>
              <Box id="total_sales_by_channel_header_contribution" className="invListTableHeaderCol sixColumn textRight">
                <ParagraphText id="total_sales_by_channel_header_label_contribution" className="fontSize12 fontWeight500 newLightColor">{I18n.contribution}</ParagraphText>
              </Box>
            </Box>
          )}
          <Box id="total_sales_by_channel_scrollable" className={pageStyles.pageBody}>
            {loading
              ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <Box key={i} className="invoiceListTableBody defaultCursorImportant">
                    <Box className="invListTableHeaderCol sixColumn textLeft">
                      <Skeleton variant="rounded" animation="wave" width={90} height={16} sx={{ borderRadius: "6px" }} />
                    </Box>
                    <Box className="invListTableHeaderCol sixColumn textLeft">
                      <Skeleton variant="rounded" animation="wave" width={36} height={16} sx={{ borderRadius: "6px" }} />
                    </Box>
                    <Box className="invListTableHeaderCol sixColumn textLeft">
                      <Skeleton variant="rounded" animation="wave" width={44} height={16} sx={{ borderRadius: "6px" }} />
                    </Box>
                    <Box className="invListTableHeaderCol sixColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Skeleton variant="rounded" animation="wave" width={80} height={16} sx={{ borderRadius: "6px" }} />
                    </Box>
                    <Box className="invListTableHeaderCol sixColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Skeleton variant="rounded" animation="wave" width={70} height={16} sx={{ borderRadius: "6px" }} />
                    </Box>
                    <Box className="invListTableHeaderCol sixColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Skeleton variant="rounded" animation="wave" width={32} height={16} sx={{ borderRadius: "6px" }} />
                    </Box>
                  </Box>
                ))
              : salesByChannel.length === 0
                ? (
                  <FilterNonIdeal
                    title={I18n.no_transactions_matching_those_filters}
                    subTitle={`${I18n.try_adjusting_filters} `}
                    subTitleText={I18n.view_yearly_data}
                    subTitleTextOnClick={handleResetFilter}
                    subTitleTextId="total_sales_by_channel_filter_view_yearly"
                  />
                )
                : salesByChannel.map(({ channel, transactions, units_sold, total_sales, avg_order_value, contribution }, i) => (
                  <Box key={channel} id={`total_sales_by_channel_row_${i}`} className="invoiceListTableBody defaultCursorImportant">
                    <Box id={`total_sales_by_channel_row_${i}_channel`} className="invListTableHeaderCol sixColumn textLeft">
                      <ParagraphText id={`total_sales_by_channel_cell_${i}_channel`} className="fontSize14 newFontColor">{channel}</ParagraphText>
                    </Box>
                    <Box id={`total_sales_by_channel_row_${i}_orders`} className="invListTableHeaderCol sixColumn textLeft">
                      <ParagraphText id={`total_sales_by_channel_cell_${i}_orders`} className="fontSize14 newFontColor">{transactions}</ParagraphText>
                    </Box>
                    <Box id={`total_sales_by_channel_row_${i}_units_sold`} className="invListTableHeaderCol sixColumn textLeft">
                      <ParagraphText id={`total_sales_by_channel_cell_${i}_units_sold`} className="fontSize14 newFontColor">{units_sold}</ParagraphText>
                    </Box>
                    <Box id={`total_sales_by_channel_row_${i}_total_sales`} className="invListTableHeaderCol sixColumn textRight">
                      <ParagraphText id={`total_sales_by_channel_cell_${i}_total_sales`} className="fontSize14 newFontColor">{formatReportCurrency(total_sales)}</ParagraphText>
                    </Box>
                    <Box id={`total_sales_by_channel_row_${i}_avg_order_value`} className="invListTableHeaderCol sixColumn textRight">
                      <ParagraphText id={`total_sales_by_channel_cell_${i}_avg_order_value`} className="fontSize14 newFontColor">{formatReportCurrency(avg_order_value)}</ParagraphText>
                    </Box>
                    <Box id={`total_sales_by_channel_row_${i}_contribution`} className="invListTableHeaderCol sixColumn textRight">
                      <ParagraphText id={`total_sales_by_channel_cell_${i}_contribution`} className="fontSize14 newFontColor">{contribution}%</ParagraphText>
                    </Box>
                  </Box>
                ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default TotalSalesByChannel;
